import type {
  ApprovalRequestId,
  CmmdOrgProviderPolicy,
  EnvironmentId,
  ModelSelection,
  ProviderApprovalDecision,
  ProviderInteractionMode,
  ProviderKind,
  RuntimeMode,
  ServerProvider,
  ThreadId,
} from "@cmmd-forge/contracts";
import type { ReactNode } from "react";

import type { ContextWindowSnapshot } from "~/lib/contextWindow";
import { CompactComposerControlsMenu } from "./CompactComposerControlsMenu";
import { ComposerControlSeparator } from "./ComposerControl";
import {
  ComposerStashToolbarControl,
  useHasStashEntries,
  type ComposerStashControlProps,
} from "./ComposerStashMenu";
import { ComposerFooterModeControls } from "./ComposerFooterModeControls";
import { ComposerFooterPrimaryActions } from "./ComposerFooterPrimaryActions";
import { ComposerImageAttachmentButton } from "./ComposerImageAttachmentButton";
import { ComposerPendingApprovalActions } from "./ComposerPendingApprovalActions";
import { ComposerTaskAnchorPicker } from "./ComposerTaskAnchorPicker";
import type { PlanUsageThread } from "./PlanUsageContextWindowMeter";
import { ProviderModelPicker } from "./ProviderModelPicker";
import {
  ComposerAccountModelPicker,
  type ComposerAccountSelection,
} from "./ComposerAccountModelPicker";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";

interface ComposerFooterLeftProps {
  readonly accountSelection?: ComposerAccountSelection;
  /** Compact footer: decided by the Composer's own width, not by the viewport. */
  readonly footerCompact: boolean;
  /**
   * Below `md`, where the controls strip is `display: none`. The two controls
   * the strip's overflow menu owns have to come back to the footer there, and
   * only there: from `md` up a Composer under 620px is compact *and* has a
   * visible strip, so offering them in both places would print each of them
   * twice on the same screen.
   */
  readonly isMobileViewport: boolean;
  readonly cmmdProjectId: string | null;
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId | null;
  readonly taskAnchorId: string | null;
  readonly taskAnchorDisabled: boolean;
  readonly selectedProvider: ProviderKind;
  readonly selectedModel: string;
  readonly lockedProvider: ProviderKind | null;
  readonly providerStatuses: ServerProvider[];
  /** Why the picker may withhold each provider it shows, per `ProviderModelPicker`. */
  readonly providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  readonly modelOptionsByProvider: Record<
    ProviderKind,
    ReadonlyArray<ServerProvider["models"][number]>
  >;
  readonly orgProviderPolicy: CmmdOrgProviderPolicy | null;
  readonly modelPickerIconClassName: string | null;
  readonly interactionMode: ProviderInteractionMode;
  readonly runtimeMode: RuntimeMode;
  readonly showPlanSidebarToggle: boolean;
  readonly planSidebarLabel: string;
  readonly planSidebarOpen: boolean;
  readonly providerTraitsPicker: ReactNode;
  readonly providerTraitsMenuContent: ReactNode;
  readonly onProviderModelChange: (provider: ProviderKind, model: string) => void;
  readonly onToggleInteractionMode: () => void;
  readonly onRuntimeModeChange: (mode: RuntimeMode) => void;
  readonly onTogglePlanSidebar: () => void;
  readonly stash?: ComposerStashControlProps | null;
}

interface ComposerFooterRightProps {
  readonly primaryActionsCompact: boolean;
  readonly pendingApprovalRequestId: ApprovalRequestId | null;
  readonly isRespondingToApproval: boolean;
  readonly onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<void>;
  readonly hasPendingUserInput: boolean;
  readonly activeContextWindow: ContextWindowSnapshot | null;
  readonly activeThreadModelSelection: ModelSelection | null | undefined;
  readonly activeThreadRateLimitStatus: PlanUsageThread["rateLimitStatus"];
  readonly pendingAction: {
    questionIndex: number;
    isLastQuestion: boolean;
    canAdvance: boolean;
    isResponding: boolean;
    isComplete: boolean;
  } | null;
  readonly isRunning: boolean;
  readonly showPlanFollowUpPrompt: boolean;
  readonly promptHasText: boolean;
  readonly isSendBusy: boolean;
  readonly isConnecting: boolean;
  readonly isPreparingWorktree: boolean;
  readonly hasSendableContent: boolean;
  readonly isComposerDisabled: boolean;
  readonly onAttachImages: (files: ReadonlyArray<File>) => void;
  readonly onPreviousPendingQuestion: () => void;
  readonly onInterrupt: () => void;
  readonly onImplementPlanInNewThread: () => void;
}

/**
 * The CMMD board task a thread is anchored to, with its trailing separator.
 *
 * Only in the Compact footer, and only on a phone. The visible footer is
 * t3code's, which has no task anchor, so the anchor moved into the controls
 * strip's overflow menu. That strip is `hidden md:flex`, so below `md` the
 * overflow menu is not on screen and this is the only way to reach the anchor.
 * From `md` up the strip is there whatever the Composer's width, so a narrow
 * desktop Composer offers the anchor once, in the strip, and not twice.
 *
 * Rendered only for a saved thread on a numeric CMMD project id, which are the
 * two things the picker needs to list or attach a task.
 */
function ComposerFooterTaskAnchor({
  footerCompact,
  isMobileViewport,
  cmmdProjectId,
  environmentId,
  activeThreadId,
  taskAnchorId,
  taskAnchorDisabled,
}: Pick<
  ComposerFooterLeftProps,
  | "footerCompact"
  | "isMobileViewport"
  | "cmmdProjectId"
  | "environmentId"
  | "activeThreadId"
  | "taskAnchorId"
  | "taskAnchorDisabled"
>) {
  if (!footerCompact || !isMobileViewport) return null;
  if (cmmdProjectId === null || activeThreadId === null) return null;
  return (
    <>
      <ComposerTaskAnchorPicker
        projectId={cmmdProjectId}
        environmentId={environmentId}
        threadId={activeThreadId}
        currentTaskId={taskAnchorId}
        disabled={taskAnchorDisabled}
      />
      <ComposerControlSeparator />
    </>
  );
}

function ComposerFooterModelPicker({
  accountSelection,
  footerCompact,
  selectedProvider,
  selectedModel,
  lockedProvider,
  providerStatuses,
  providerAvailability,
  modelOptionsByProvider,
  orgProviderPolicy,
  modelPickerIconClassName,
  onProviderModelChange,
}: Pick<
  ComposerFooterLeftProps,
  | "accountSelection"
  | "footerCompact"
  | "selectedProvider"
  | "selectedModel"
  | "lockedProvider"
  | "providerStatuses"
  | "providerAvailability"
  | "modelOptionsByProvider"
  | "orgProviderPolicy"
  | "modelPickerIconClassName"
  | "onProviderModelChange"
>) {
  const pickerProps = {
    compact: footerCompact,
    provider: selectedProvider,
    model: selectedModel,
    lockedProvider,
    providers: providerStatuses,
    providerAvailability,
    modelOptionsByProvider,
    orgProviderPolicy,
    ...(modelPickerIconClassName ? { activeProviderIconClassName: modelPickerIconClassName } : {}),
    onProviderModelChange,
  };
  return accountSelection ? (
    <ComposerAccountModelPicker {...pickerProps} accountSelection={accountSelection} />
  ) : (
    <ProviderModelPicker {...pickerProps} />
  );
}

/**
 * The mode cluster, either in place or folded into the Compact footer's
 * overflow menu.
 *
 * The two branches are not the same list. The visible row is t3code's, so the
 * plan sidebar toggle is not on it; it lives in the controls strip's overflow
 * menu. The Compact footer's menu carries the toggle only below `md`, where
 * that strip is `display: none` and the menu is the one path to it. From `md`
 * up the strip is on screen at every Composer width, so `activePlan` goes false
 * here and the toggle has exactly one home.
 */
function ComposerFooterControls({
  footerCompact,
  isMobileViewport,
  interactionMode,
  runtimeMode,
  showPlanSidebarToggle,
  planSidebarLabel,
  planSidebarOpen,
  providerTraitsPicker,
  providerTraitsMenuContent,
  onToggleInteractionMode,
  onRuntimeModeChange,
  onTogglePlanSidebar,
  stash,
}: Pick<
  ComposerFooterLeftProps,
  | "footerCompact"
  | "isMobileViewport"
  | "interactionMode"
  | "runtimeMode"
  | "showPlanSidebarToggle"
  | "planSidebarLabel"
  | "planSidebarOpen"
  | "providerTraitsPicker"
  | "providerTraitsMenuContent"
  | "onToggleInteractionMode"
  | "onRuntimeModeChange"
  | "onTogglePlanSidebar"
  | "stash"
>) {
  const hasStashEntries = useHasStashEntries();
  if (footerCompact) {
    return (
      <CompactComposerControlsMenu
        activePlan={showPlanSidebarToggle && isMobileViewport}
        interactionMode={interactionMode}
        planSidebarLabel={planSidebarLabel}
        planSidebarOpen={planSidebarOpen}
        runtimeMode={runtimeMode}
        traitsMenuContent={providerTraitsMenuContent}
        stash={stash ?? null}
        onToggleInteractionMode={onToggleInteractionMode}
        onTogglePlanSidebar={onTogglePlanSidebar}
        onRuntimeModeChange={onRuntimeModeChange}
      />
    );
  }
  // t3code's order, left to right: model picker, reasoning effort, then the
  // runtime mode and Build/Plan cluster. The effort picker brings its own
  // leading separator so a model with no options hides the hairline with it.
  // No plan sidebar toggle: t3code has none, so it moved into the controls
  // strip's overflow menu.
  return (
    <>
      {providerTraitsPicker}
      <ComposerFooterModeControls
        interactionMode={interactionMode}
        runtimeMode={runtimeMode}
        onToggleInteractionMode={onToggleInteractionMode}
        onRuntimeModeChange={onRuntimeModeChange}
      />
      {stash && hasStashEntries ? (
        <>
          <ComposerControlSeparator />
          <ComposerStashToolbarControl {...stash} />
        </>
      ) : null}
    </>
  );
}

/**
 * The Composer footer's leading cluster: the model picker, provider traits and
 * the mode controls, plus the task anchor while the footer is compact.
 *
 * Why a component and not JSX left in ChatComposer: the orchestrator was past
 * the file-size cap, so every later footer change had to buy its space back
 * somewhere else in the file. Nothing here owns state. Everything it shows is a
 * prop, so the footer can be rendered from a test without the orchestrator.
 */
function ComposerFooterLeft(props: ComposerFooterLeftProps) {
  return (
    <>
      <ComposerFooterTaskAnchor {...props} />
      <ComposerFooterModelPicker {...props} />
      <ComposerFooterControls {...props} />
    </>
  );
}

/**
 * Image attachment, absent while the Composer is answering a pending user
 * input. The same condition suppresses image previews and terminal contexts on
 * the surface, so a Draft attachment made here would have nowhere to show.
 */
function ComposerFooterAttachment({
  hasPendingUserInput,
  isComposerDisabled,
  isConnecting,
  onAttachImages,
}: Pick<
  ComposerFooterRightProps,
  "hasPendingUserInput" | "isComposerDisabled" | "isConnecting" | "onAttachImages"
>) {
  if (hasPendingUserInput) return null;
  return (
    <ComposerImageAttachmentButton
      disabled={isComposerDisabled || isConnecting}
      onFilesSelected={onAttachImages}
    />
  );
}

/** Image attachment beside the send and stop actions with the context meter. */
function ComposerFooterSendCluster(
  props: Omit<
    ComposerFooterRightProps,
    "pendingApprovalRequestId" | "isRespondingToApproval" | "onRespondToApproval"
  >,
) {
  return (
    <>
      <ComposerFooterAttachment {...props} />
      <ComposerFooterPrimaryActions
        compact={props.primaryActionsCompact}
        activeContextWindow={props.activeContextWindow}
        activeThreadModelSelection={props.activeThreadModelSelection}
        activeThreadRateLimitStatus={props.activeThreadRateLimitStatus}
        pendingAction={props.pendingAction}
        isRunning={props.isRunning}
        showPlanFollowUpPrompt={props.showPlanFollowUpPrompt}
        promptHasText={props.promptHasText}
        isSendBusy={props.isSendBusy}
        isConnecting={props.isConnecting}
        isPreparingWorktree={props.isPreparingWorktree}
        hasSendableContent={props.hasSendableContent}
        isComposerDisabled={props.isComposerDisabled}
        onPreviousPendingQuestion={props.onPreviousPendingQuestion}
        onInterrupt={props.onInterrupt}
        onImplementPlanInNewThread={props.onImplementPlanInNewThread}
      />
    </>
  );
}

/**
 * The Composer footer's trailing cluster.
 *
 * A pending approval replaces the cluster rather than sitting beside it. The
 * approval actions already carry their own cancel, and the context meter and
 * send button belong to a turn the person has not authorised yet.
 */
function ComposerFooterRight(props: ComposerFooterRightProps) {
  if (props.pendingApprovalRequestId !== null) {
    return (
      <ComposerPendingApprovalActions
        requestId={props.pendingApprovalRequestId}
        isResponding={props.isRespondingToApproval}
        onRespondToApproval={props.onRespondToApproval}
      />
    );
  }
  return <ComposerFooterSendCluster {...props} />;
}

/**
 * ComposerSurface owns two footer slots in two different boxes, so the footer
 * is one module with two entry points rather than one element. Grouping them
 * keeps the pair discoverable from either side.
 */
export const ComposerFooter = {
  Left: ComposerFooterLeft,
  Right: ComposerFooterRight,
} as const;
